import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Loader2, Search } from 'lucide-react';
import {
    isGooglePlacesAvailable,
    searchAddresses,
    getAddressComponents
} from '../../services/googlePlacesService';

/**
 * Address autocomplete component using Google Places API
 * For forms that need full address components (profile, contacts, registration, etc.)
 *
 * @param {Object} props
 * @param {string} props.label - Input label
 * @param {string} props.value - Input value
 * @param {Function} props.onChange - Change handler (field, value)
 * @param {Function} props.onCoordinatesChange - Callback for lat/lng changes
 * @param {Function} props.onAddressComponentsChange - Callback for structured address data
 * @param {string} props.error - Validation error message
 * @param {string} props.apiKey - Google Maps API key (kept for compatibility, not used)
 * @param {boolean} props.hideLabel - Hide the built-in label (when parent provides its own)
 * @param {boolean} props.showSearchIcon - Show a search icon inside the input
 */
const AddressAutocomplete = ({ label, value, onChange, onCoordinatesChange, onAddressComponentsChange, error, apiKey, hideLabel = false, showSearchIcon = false }) => {
    const manualInputRef = useRef(null);
    const dropdownRef = useRef(null);
    const isUserSelectionRef = useRef(false);
    const searchTimeoutRef = useRef(null);
    const [inputValue, setInputValue] = useState(value || '');
    const [isSearching, setIsSearching] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const [isApiLoaded, setIsApiLoaded] = useState(false);

    // Check if API is loaded
    useEffect(() => {
        const checkApi = () => {
            if (isGooglePlacesAvailable()) {
                setIsApiLoaded(true);
                return true;
            }
            return false;
        };

        if (checkApi()) return;

        const interval = setInterval(() => {
            if (checkApi()) {
                clearInterval(interval);
            }
        }, 100);

        const timeout = setTimeout(() => {
            clearInterval(interval);
        }, 15000);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, []);

    // Search for addresses with debounce
    const searchForAddresses = async (text) => {
        if (!text || text.length < 2 || !isApiLoaded) {
            setSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        setIsSearching(true);

        try {
            const results = await searchAddresses(text);

            if (results && results.length > 0) {
                setSuggestions(results);
                setShowSuggestions(true);
            } else {
                setSuggestions([]);
                setShowSuggestions(false);
            }
        } catch (error) {
            console.error('Error searching addresses:', error);
            setSuggestions([]);
        } finally {
            setIsSearching(false);
        }
    };

    // Update internal state when external value changes
    useEffect(() => {
        // Skip update if the change came from user selection
        if (isUserSelectionRef.current) {
            isUserSelectionRef.current = false;
            return;
        }

        const newValue = value || '';
        setInputValue(newValue);

        // If there's a pre-filled value from OCR, search to get suggestions
        if (newValue.trim() !== '' && newValue !== inputValue && isApiLoaded) {
            setTimeout(() => {
                searchForAddresses(newValue);
            }, 500);
        }
    }, [value, isApiLoaded]);

    // Handle suggestion selection
    const handleSuggestionClick = async (suggestion) => {
        const selectedAddress = suggestion.description;

        // Hide suggestions dropdown first
        setShowSuggestions(false);
        setSuggestions([]);

        // Mark that this change came from user selection
        isUserSelectionRef.current = true;

        // Update local state
        setInputValue(selectedAddress);

        // Notify parent component
        if (onChange) {
            onChange('address', selectedAddress);
        }

        // Fetch place details for coordinates and components
        if (suggestion.placeId) {
            try {
                const components = await getAddressComponents(suggestion.placeId);

                if (components) {
                    // Call coordinates handler
                    if (onCoordinatesChange && components.lat && components.lng) {
                        onCoordinatesChange(components.lat, components.lng);
                    }

                    // Call address components handler
                    if (onAddressComponentsChange) {
                        onAddressComponentsChange(components);
                    }
                }
            } catch (error) {
                console.error('Error fetching place details:', error);
            }
        }
    };

    // Handle manual input changes with debounce
    const handleManualInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        onChange?.('address', newValue);

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Debounce search
        if (newValue && newValue.length > 2) {
            searchTimeoutRef.current = setTimeout(() => {
                searchForAddresses(newValue);
            }, 300);
        } else {
            setShowSuggestions(false);
            setSuggestions([]);
        }
    };

    // Calculate dropdown position (use fixed positioning relative to viewport)
    const updateDropdownPosition = () => {
        if (manualInputRef.current) {
            const rect = manualInputRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width
            });
        }
    };

    // Update position when suggestions appear and on scroll/resize
    useEffect(() => {
        if (showSuggestions) {
            updateDropdownPosition();

            // Listen for scroll and resize to update position
            const handleScrollOrResize = () => {
                updateDropdownPosition();
            };

            window.addEventListener('scroll', handleScrollOrResize, true);
            window.addEventListener('resize', handleScrollOrResize);

            return () => {
                window.removeEventListener('scroll', handleScrollOrResize, true);
                window.removeEventListener('resize', handleScrollOrResize);
            };
        }
    }, [showSuggestions]);

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const clickedOutsideInput = manualInputRef.current && !manualInputRef.current.contains(event.target);
            const clickedOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(event.target);

            if (clickedOutsideInput && clickedOutsideDropdown) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="relative">
            {!hideLabel && (
                <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <MapPin className="w-4 h-4 mr-2 text-cabaza-purple" />
                    {label}
                </label>
            )}
            <div className="relative">
                {showSearchIcon && (
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 z-10">
                        <Search className="w-4 h-4" />
                    </div>
                )}
                <input
                    ref={manualInputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleManualInputChange}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    data-form-type="other"
                    className={`w-full ${showSearchIcon ? 'pl-9' : 'px-4'} py-2 pr-10 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-cabaza-purple/50 focus:border-cabaza-purple bg-slate-50 text-slate-800 placeholder-slate-400 transition-all ${
                        error ? 'border-cabaza-pink' : 'border-slate-200'
                    }`}
                    placeholder={isApiLoaded ? (label || 'Rechercher une adresse...') : 'Chargement...'}
                    disabled={!isApiLoaded}
                />
                {(!isApiLoaded || isSearching) && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                    </div>
                )}
            </div>

            {/* Suggestions dropdown via portal */}
            {showSuggestions && suggestions.length > 0 && createPortal(
                <div
                    ref={dropdownRef}
                    className="fixed z-[10002] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                    style={{
                        top: dropdownPosition.top,
                        left: dropdownPosition.left,
                        width: dropdownPosition.width
                    }}
                >
                    {suggestions.map((suggestion, index) => (
                        <div
                            key={suggestion.placeId}
                            onMouseDown={(e) => {
                                e.preventDefault();
                                handleSuggestionClick(suggestion);
                            }}
                            className={`px-4 py-3 cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20 ${
                                index !== 0 ? 'border-t border-gray-200 dark:border-gray-700' : ''
                            }`}
                        >
                            <div className="flex items-start">
                                <MapPin className="w-4 h-4 mr-2 mt-0.5 text-cabaza-purple flex-shrink-0" />
                                <span className="text-sm text-gray-900 dark:text-white">{suggestion.description}</span>
                            </div>
                        </div>
                    ))}
                </div>,
                document.body
            )}

            {error && <p className="mt-1 text-xs text-cabaza-pink">{error}</p>}
        </div>
    );
};

export default AddressAutocomplete;
